import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
 Modal, Button, ListGroup, Form,
} from 'react-bootstrap';
import classnames from 'classnames';

import qapi from 'views_shared/app/javascript/api';
import ImageCropped from 'views_shared/app/javascript/components/ImageCropped';
import useTeammateSearch from '../useTeammateSearch';

const PAGE_SIZE = 25;

function AddToConversationButton({
  conversationId,
  currentTeamId,
  participantSlugs,
  representedExperts,
  onSuccess,
  show: controlledShow,
  onShowChange,
  buttonClassName,
}) {
  // Support both controlled (parent passes `show`/`onShowChange`) and
  // uncontrolled modes. This lets the mobile dropdown open the same modal
  // without duplicating the component tree.
  const [internalShow, setInternalShow] = useState(false);
  const isControlled = controlledShow !== undefined;
  const showModal = isControlled ? controlledShow : internalShow;
  const setShowModal = (value) => {
    if (isControlled) {
      onShowChange?.(value);
    } else {
      setInternalShow(value);
    }
  };

  const [searchQuery, setSearchQuery] = useState('');
  // Persists across query changes; only handleClose resets it.
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const {
    teammates: searchedTeammates,
    total: teammateTotal,
    loading: teammatesLoading,
    error: teammatesError,
    retry: retryTeammates,
  } = useTeammateSearch(currentTeamId, searchQuery, participantSlugs, pageSize);

  // Map (not Set) so a selection survives a query change with name/slug intact.
  const [selectedTeammates, setSelectedTeammates] = useState(new Map());
  const [selectedExpertIds, setSelectedExpertIds] = useState(new Set());
  const [isAdding, setIsAdding] = useState(false);

  const hasSelection = selectedTeammates.size > 0 || selectedExpertIds.size > 0;

  const toggleTeammate = (profile) => {
    setSelectedTeammates((prev) => {
      const next = new Map(prev);
      if (next.has(profile.profile_id)) next.delete(profile.profile_id);
      else next.set(profile.profile_id, profile);
      return next;
    });
  };

  const toggleExpert = (entityId) => {
    setSelectedExpertIds((prev) => {
      const next = new Set(prev);
      if (next.has(entityId)) next.delete(entityId);
      else next.add(entityId);
      return next;
    });
  };

  const handleClose = () => {
    setShowModal(false);
    setSelectedTeammates(new Map());
    setSelectedExpertIds(new Set());
    setSearchQuery('');
    setPageSize(PAGE_SIZE);
  };

  const handleAddToConversation = async () => {
    setIsAdding(true);

    const teammateValues = [...selectedTeammates.values()];
    const selectedExperts = representedExperts.filter(
      (e) => e.profile_id && selectedExpertIds.has(e.promoted_entity_id),
    );

    const allNames = [...teammateValues.map((t) => t.name), ...selectedExperts.map((e) => e.promoted_entity_name)];

    // Add receivers sequentially to avoid race conditions on the same conversation
    const receiversToAdd = [...teammateValues.map((t) => t.profile_id), ...selectedExperts.map((e) => e.profile_id)];

    let addedCount = 0;

    try {
      await receiversToAdd.reduce(
        (chain, profileId) => chain.then(async () => {
          const result = await qapi.conversations.addReceiver(conversationId, profileId);
          // createResource returns { error } on non-2xx instead of throwing; route to the
          // catch so the user actually sees the failure (e.g. 422 on peer DMs).
          if (result?.error) {
            throw result.error;
          }
          addedCount += 1;
        }),
        Promise.resolve(),
      );

      handleClose();

      const toastBody = allNames.length === 1
        ? `${allNames[0]} has been added to the conversation.`
        : `${allNames.join(', ')} have been added to the conversation.`;
      globalThis.Qwoted.showToast({ toastTitle: 'Added to conversation', toastLink: null, toastBody });

      onSuccess();
    } catch (error) {
      if (addedCount > 0) onSuccess();

      // Prefer the API's explicit detail (e.g. "Participants cannot be added to a direct
      // message") when present; fall back to the partial-success / generic message.
      const serverDetail = error?.body?.errors?.[0]?.detail;
      const toastBody = serverDetail
        || (addedCount > 0
          ? `Only ${addedCount} of ${receiversToAdd.length} people could be added. Please try again.`
          : 'Something went wrong. Please try again.');
      globalThis.Qwoted.showToast({ toastTitle: 'Error adding to conversation', toastLink: null, toastBody });
    } finally {
      setIsAdding(false);
    }
  };

  const trimmedQuery = searchQuery.trim();
  const isTruncated = teammateTotal > searchedTeammates.length;
  // Hidden on small teams the user can scan; an active query forces it back.
  const SEARCH_THRESHOLD = 10;
  const showSearchInput = trimmedQuery.length > 0 || teammateTotal > SEARCH_THRESHOLD;

  // Selected pinned at top so they survive query changes; filter to avoid double-render.
  const pinnedTeammates = [...selectedTeammates.values()];
  const remainingTeammates = searchedTeammates.filter((t) => !selectedTeammates.has(t.profile_id));
  const showEmptyState = !teammatesLoading
    && remainingTeammates.length === 0
    && pinnedTeammates.length === 0
    && trimmedQuery.length > 0;

  return (
    <>
      <Button
        className={classnames('text-nowrap fw-medium', buttonClassName)}
        variant="secondary"
        onClick={() => setShowModal(true)}
      >
        <i className="fa-solid fa-user-plus paragraph-md" /> Add to Conversation
      </Button>

      <Modal show={showModal} onHide={handleClose} id="add-to-conversation-modal" scrollable>
        <Modal.Header closeButton className="px-4 py-4 border-0 align-items-start">
          <div>
            <Modal.Title className="paragraph-lg fw-bold">Add People to the Conversation</Modal.Title>
            <p className="text-secondary paragraph-sm mb-0 mt-1">
              They can view all past messages and will receive new ones as they&apos;re sent.
            </p>
          </div>
        </Modal.Header>
        <Modal.Body className="px-4 py-0">
          {representedExperts.length > 0 && (
            <>
              <p className="paragraph-md fw-bold mb-3">Represented Experts</p>
              <ListGroup variant="flush" className="border border-accent-blue-10 rounded-2 mb-4">
                {representedExperts.map((expert) => {
                  const isSelected = selectedExpertIds.has(expert.promoted_entity_id);
                  return (
                    <ListGroup.Item key={expert.promoted_entity_slug} className="d-flex align-items-center">
                      <ImageCropped
                        roundedCircle
                        wrapperClasses="me-3"
                        src={expert.promoted_entity_profile_image_url}
                        alt={expert.promoted_entity_name}
                        size={42}
                        fallbackToInitials
                      />
                      <div className="flex-grow-1 me-3 text-truncate" style={{ minWidth: 0 }}>
                        <p className="paragraph-md fw-bold mb-0 text-truncate">{expert.promoted_entity_name}</p>
                        <p className="paragraph-sm text-secondary mb-0 text-truncate">
                          {expert.promoted_expert_job_title}
                        </p>
                      </div>
                      {expert.profile_id && (
                        <Button
                          className={classnames('px-3 text-nowrap flex-shrink-0 ms-auto rounded-2', {
                            'bg-success-10 border-success-30': isSelected,
                          })}
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => toggleExpert(expert.promoted_entity_id)}
                        >
                          <i className={`fa-solid ${isSelected ? 'fa-check' : 'fa-user-plus'} me-1`} />
                          {isSelected ? 'Selected' : 'Select'}
                        </Button>
                      )}
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
            </>
          )}

          <p className="paragraph-md fw-bold mb-3">Other teammates</p>
          {showSearchInput && (
            <Form.Control
              type="search"
              placeholder="Search teammates by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="mb-3"
              aria-label="Search teammates"
            />
          )}

          {showEmptyState ? (
            <p className="text-secondary paragraph-sm mb-0">No teammates match &ldquo;{trimmedQuery}&rdquo;.</p>
          ) : (
            <>
              {(pinnedTeammates.length > 0 || remainingTeammates.length > 0) && (
                <ListGroup variant="flush" className="border border-accent-blue-10 rounded-2">
                  {[...pinnedTeammates, ...remainingTeammates].map((profile) => {
                    const isSelected = selectedTeammates.has(profile.profile_id);
                    return (
                      <ListGroup.Item key={profile.slug} className="d-flex align-items-center">
                        <ImageCropped
                          roundedCircle
                          wrapperClasses="me-3"
                          src={profile.profile_image_url}
                          alt={profile.name}
                          size={42}
                          fallbackToInitials
                        />
                        <div className="flex-grow-1 me-3 text-truncate" style={{ minWidth: 0 }}>
                          <p className="paragraph-md fw-bold mb-0 text-truncate">{profile.name}</p>
                          <p className="paragraph-sm text-secondary mb-0 text-truncate">{profile.headline}</p>
                        </div>
                        <Button
                          className={classnames('px-3 text-nowrap flex-shrink-0 ms-auto rounded-2', {
                            'bg-success-10 border-success-30': isSelected,
                          })}
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => toggleTeammate(profile)}
                        >
                          <i className={`fa-solid ${isSelected ? 'fa-check' : 'fa-user-plus'} me-1`} />
                          {isSelected ? 'Selected' : 'Select'}
                        </Button>
                      </ListGroup.Item>
                    );
                  })}
                </ListGroup>
              )}
              {teammatesError && !teammatesLoading && (
                <div className="text-center mt-3">
                  <p className="text-danger paragraph-sm mb-2">Couldn&apos;t load teammates.</p>
                  <Button variant="link" size="sm" className="p-0" onClick={retryTeammates}>
                    Retry
                  </Button>
                </div>
              )}
              {isTruncated && !teammatesError && (
                <div className="text-center mt-3">
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0"
                    disabled={teammatesLoading}
                    onClick={() => setPageSize((n) => n + PAGE_SIZE)}
                  >
                    {teammatesLoading ? 'Loading...' : 'Show more'}
                  </Button>
                  <p className="text-secondary paragraph-sm mb-0 mt-1">
                    Showing {searchedTeammates.length} of {teammateTotal}
                  </p>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer className="px-4 py-4 border-0">
          <Button className="py-2" variant="outline-secondary" onClick={handleClose}>
            Close
          </Button>
          <Button
            className="py-2"
            variant="primary"
            disabled={!hasSelection || isAdding}
            onClick={handleAddToConversation}
          >
            {isAdding ? 'Adding...' : 'Add to Conversation'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

AddToConversationButton.propTypes = {
  conversationId: PropTypes.string.isRequired,
  currentTeamId: PropTypes.number,
  participantSlugs: PropTypes.arrayOf(PropTypes.string).isRequired,
  representedExperts: PropTypes.arrayOf(
    PropTypes.shape({
      promoted_entity_id: PropTypes.number.isRequired,
      promoted_entity_slug: PropTypes.string.isRequired,
      promoted_entity_name: PropTypes.string.isRequired,
      promoted_entity_profile_image_url: PropTypes.string,
      promoted_expert_job_title: PropTypes.string,
      profile_id: PropTypes.number,
    }),
  ).isRequired,
  onSuccess: PropTypes.func.isRequired,
  show: PropTypes.bool,
  onShowChange: PropTypes.func,
  buttonClassName: PropTypes.string,
};

export default AddToConversationButton;
